<template>
  <v-card
    class="voice-desktop-card"
    color="background"
    variant="elevated"
    elevation="4"
    @click="emitEdit"
  >
    <div class="voice-shell">
      <div class="voice-header">
        <div class="voice-header-main">
          <v-avatar size="48" rounded="lg" :style="{ backgroundColor: brandColor }" class="voice-avatar">
            <img
              v-if="showStandaloneIcon"
              :src="iconSrc"
              :alt="`${voice.providerKey} icon`"
              class="voice-avatar-image"
              @error="onIconError"
            >
            <span v-else class="voice-initials">{{ voiceInitials }}</span>
          </v-avatar>
          <div class="voice-title-wrap">
            <div class="voice-title">{{ voice.name }}</div>
            <div class="voice-subtitle">{{ subtitleText }}</div>
          </div>
        </div>

        <div class="voice-header-actions">
          <v-tooltip text="Edit Voice" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-pencil-outline"
                size="small"
                variant="plain"
                @click.stop="emitEdit"
              />
            </template>
          </v-tooltip>
        </div>
      </div>

      <div class="voice-chip-row">
        <v-chip
          v-for="label in labelValues"
          :key="label"
          size="small"
          variant="tonal"
          class="voice-chip"
        >
          {{ label }}
        </v-chip>
        <v-chip v-if="voice.responseFormat" size="small" variant="outlined" class="voice-chip">
          {{ voice.responseFormat }}
        </v-chip>
      </div>

      <div class="voice-spec-grid">
        <div class="voice-spec">
          <div class="voice-spec-k">Provider Voice</div>
          <div class="voice-spec-v voice-spec-v-mono">{{ voice.providerVoiceName || voice.voiceId }}</div>
        </div>
        <div class="voice-spec">
          <div class="voice-spec-k">Description</div>
          <div class="voice-spec-v">{{ voice.description || subtitleText }}</div>
        </div>
        <div class="voice-spec">
          <div class="voice-spec-k">Format</div>
          <div class="voice-spec-v">{{ voice.responseFormat || 'Default' }}</div>
        </div>
        <div class="voice-spec">
          <div class="voice-spec-k">Speed</div>
          <div class="voice-spec-v">{{ speedLabel }}</div>
        </div>
      </div>

      <div class="voice-footer">
        <v-btn
          class="voice-edit-btn"
          color="primary"
          variant="outlined"
          @click.stop="emitEdit"
        >
          Edit
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { VoiceConfig } from '@/types/voices'
import { getProviderBrandColor, getProviderIconPath } from '@/utils/providerIcons'

@Options({
  props: {
    voice: Object
  },
  emits: ['edit']
})
export default class VoiceSummaryCard extends Vue {
  voice!: VoiceConfig
  iconLoadFailed = false

  get voiceInitials(): string {
    const raw = (this.voice.name || this.voice.providerVoiceName || this.voice.voiceId || 'VO').replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'VO'
  }

  get subtitleText(): string {
    const bits = [this.voice.providerKey]
    if (this.voice.providerVoiceName) bits.push(this.voice.providerVoiceName)
    else if (this.voice.description) bits.push(this.voice.description)
    return bits.join(' · ')
  }

  get labelValues(): string[] {
    const labels = this.voice.providerLabels || this.voice.labels || {}
    return Object.values(labels).filter((value): value is string => typeof value === 'string').slice(0, 4)
  }

  get speedLabel(): string {
    return typeof this.voice.speed === 'number' ? this.voice.speed.toFixed(2) : 'Default'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.voice.providerKey)
  }

  get iconSrc(): string | null {
    return getProviderIconPath(this.voice.providerKey)
  }

  get showStandaloneIcon(): boolean {
    return !!this.iconSrc && this.iconLoadFailed !== true
  }

  emitEdit() {
    this.$emit('edit', this.voice.id)
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }
}
</script>

<style scoped lang="scss">
.voice-desktop-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  height: 100%;
  min-height: 246px;
}

.voice-shell {
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.voice-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.voice-header-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.voice-avatar {
  flex-shrink: 0;
}

.voice-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 7px;
}

.voice-initials {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.voice-title-wrap {
  min-width: 0;
}

.voice-title {
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.voice-subtitle {
  margin-top: 4px;
  font-size: 0.84rem;
  color: rgba(0, 0, 0, 0.58);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.voice-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.voice-chip {
  font-size: 0.8rem;
}

.voice-spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  padding: 14px 0;
  margin-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.voice-spec-k {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 4px;
}

.voice-spec-v {
  font-size: 0.92rem;
  line-height: 1.35;
  font-weight: 600;
}

.voice-spec-v-mono {
  font-family: var(--font-mono, monospace);
  font-size: 0.84rem;
  word-break: break-word;
}

.voice-footer {
  margin-top: auto;
  padding-top: 12px;
  display: flex;
}

.voice-edit-btn {
  min-width: 120px;
}
</style>
